interface RetryOptions {
  shouldRetry?: (error: any) => boolean;
  signal?: AbortSignal;
}

export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxAttempts: number,
  initialDelayMs: number,
  options?: RetryOptions
): Promise<T> {
  const { shouldRetry, signal } = options ?? {};
  let attempt = 1;
  let lastError: any = new Error("retryWithBackoff: no attempts made");

  while (attempt <= maxAttempts) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    try {
      const result = await fn();

      // truthy result == success
      if (result) return result;

      lastError = new Error("retryWithBackoff: result was falsy");
    } catch (err) {
      if (shouldRetry && !shouldRetry(err)) throw err;
      lastError = err;
    }

    if (attempt >= maxAttempts) break;
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    const delayMs = initialDelayMs * Math.pow(2, attempt - 1);
    await new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, delayMs);
      signal?.addEventListener("abort", () => { clearTimeout(timer); resolve(); }, { once: true });
    });

    attempt++;
  }

  throw lastError;
}
